import {
  Button,
  Chip,
  Drawer,
  Group,
  MultiSelect,
  NumberInput,
  Popover,
  ScrollArea,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { IconFilter, IconSearch } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BudgetFilterKind, BudgetFilterStatus } from 'src/Hooks/Shared/useBudgetFilters';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import { MAIN_CATEGORY_LABELS } from 'src/Utils/expenseCategories';

import styles from './BudgetFilters.module.scss';
import { Props } from './BudgetFilters.types';

const formatAmount = (amount: number) =>
  `${amount.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;

const BudgetFilters = ({
  filters,
  onFiltersChange,
  onReset,
  activeFilterCount,
  availableCategories,
  availablePayers,
  summary,
}: Props) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const update = <K extends keyof typeof filters>(key: K, value: (typeof filters)[K]) =>
    onFiltersChange({ ...filters, [key]: value });

  const kindOptions: { value: BudgetFilterKind; label: string }[] = [
    { value: 'expense', label: t('expenses.filters.kind.expense', 'Dépenses') },
    { value: 'income', label: t('expenses.filters.kind.income', 'Revenus') },
    { value: 'transfer', label: t('expenses.filters.kind.transfer', 'Remboursements') },
  ];

  // The count lives in the label rather than in a Badge: the outline hover flips the button
  // to a dark background and only whitens the label, which would leave a badge sitting there
  // in its own colour. No other button in the app carries a badge counter either.
  const filtersButtonLabel =
    activeFilterCount > 0
      ? t('expenses.filters.moreWithCount', 'Filtres ({{count}})', { count: activeFilterCount })
      : t('expenses.filters.more', 'Filtres');

  const statusOptions: { value: BudgetFilterStatus; label: string }[] = [
    { value: 'ALL', label: t('expenses.filters.status.all', 'Tous') },
    { value: 'SETTLED', label: t('expenses.filters.status.settled', 'Réglé') },
    { value: 'PENDING', label: t('expenses.filters.status.pending', 'En attente') },
  ];

  const panelContent = (
    <div className={styles.panel}>
      <MultiSelect
        label={t('common.payer', 'Payeur')}
        placeholder={t('expenses.filters.payerPlaceholder', 'Tous les payeurs')}
        data={availablePayers.map(payer => ({ value: payer.id, label: payer.name }))}
        value={filters.payerIds}
        onChange={value => update('payerIds', value)}
        searchable
        clearable
      />

      <div className={styles.field}>
        <Text size="sm" fw={500}>
          {t('common.status', 'Statut')}
        </Text>
        <Chip.Group
          value={filters.status}
          onChange={value => update('status', value as BudgetFilterStatus)}
        >
          <Group gap="xs">
            {statusOptions.map(option => (
              <Chip key={option.value} value={option.value} variant="light">
                {option.label}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
      </div>

      <div className={styles.field}>
        <Text size="sm" fw={500}>
          {t('expenses.filters.kind.label', 'Nature')}
        </Text>
        <Chip.Group
          multiple
          value={filters.kinds}
          onChange={value => update('kinds', value as BudgetFilterKind[])}
        >
          <Group gap="xs">
            {kindOptions.map(option => (
              <Chip key={option.value} value={option.value} variant="light">
                {option.label}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
      </div>

      <div className={styles.field}>
        <Text size="sm" fw={500}>
          {t('common.amount', 'Montant')}
        </Text>
        <Group gap="xs" grow>
          <NumberInput
            placeholder={t('expenses.filters.amountMin', 'Min')}
            value={filters.amountMin ?? ''}
            onChange={value => update('amountMin', value === '' ? null : Number(value))}
            min={0}
            suffix=" €"
          />
          <NumberInput
            placeholder={t('expenses.filters.amountMax', 'Max')}
            value={filters.amountMax ?? ''}
            onChange={value => update('amountMax', value === '' ? null : Number(value))}
            min={0}
            suffix=" €"
          />
        </Group>
      </div>

      <Switch
        label={t('expenses.filters.onlyMine', 'Ne montrer que ce qui me concerne')}
        checked={filters.onlyMine}
        onChange={event => update('onlyMine', event.currentTarget.checked)}
      />

      {activeFilterCount > 0 && (
        <Button variant="outline" onClick={onReset}>
          {t('expenses.filters.reset', 'Réinitialiser')}
        </Button>
      )}
    </div>
  );

  return (
    <div className={styles.container}>
      <ScrollArea className={styles.scrollArea} type="auto" offsetScrollbars="x">
        <Group className={styles.group} gap="xs">
          <Chip
            checked={filters.mainCategories.length === 0}
            onChange={() => update('mainCategories', [])}
            variant="light"
          >
            {t('expenses.filters.all', 'Toutes')}
          </Chip>
          <Chip.Group
            multiple
            value={filters.mainCategories}
            onChange={value => update('mainCategories', value)}
          >
            {availableCategories.map(category => (
              <Chip key={category} value={category} variant="light">
                {t(`expenses.filters.category.${category}`, MAIN_CATEGORY_LABELS[category])}
              </Chip>
            ))}
          </Chip.Group>
        </Group>
      </ScrollArea>

      <Group className={styles.searchRow} gap="xs">
        <TextInput
          className={styles.search}
          leftSection={<IconSearch size={16} />}
          placeholder={t('expenses.filters.searchPlaceholder', 'Rechercher une opération')}
          value={filters.search}
          onChange={event => update('search', event.currentTarget.value)}
        />

        {isMobile ? (
          <>
            <Button
              variant="outline"
              leftSection={<IconFilter size={16} />}
              onClick={() => setIsPanelOpen(true)}
            >
              {filtersButtonLabel}
            </Button>
            <Drawer
              opened={isPanelOpen}
              onClose={() => setIsPanelOpen(false)}
              position="bottom"
              title={t('expenses.filters.more', 'Filtres')}
            >
              {panelContent}
            </Drawer>
          </>
        ) : (
          <Popover
            opened={isPanelOpen}
            onChange={setIsPanelOpen}
            position="bottom-end"
            width={320}
            shadow="md"
          >
            <Popover.Target>
              <Button
                variant="outline"
                leftSection={<IconFilter size={16} />}
                onClick={() => setIsPanelOpen(open => !open)}
              >
                {filtersButtonLabel}
              </Button>
            </Popover.Target>
            <Popover.Dropdown>{panelContent}</Popover.Dropdown>
          </Popover>
        )}
      </Group>

      <Text className={styles.summary} size="sm" c="dimmed">
        {t('expenses.filters.summary.count', '{{count}} opération(s)', { count: summary.count })}
        {' · '}
        {t('expenses.filters.summary.total', 'Total {{amount}}', {
          amount: formatAmount(summary.total),
        })}
        {' · '}
        {t('expenses.filters.summary.myImpact', 'Mon impact {{amount}}', {
          amount: formatAmount(summary.myImpact),
        })}
      </Text>
    </div>
  );
};

export default BudgetFilters;
